import React, { useState, useEffect, useCallback } from 'react';
import { getEstadoCuentaById, createEstadoCuenta, updateEstadoCuenta } from '../services/estadoCuentaService';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, X, ArrowLeft, RefreshCw, Check, AlertCircle, FileText, Hash } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

const EstadoCuentaForm = () => {
    const { id } = useParams();
    const isEditMode = !!id;
    const navigate = useNavigate();
    const { token } = useAuth();

    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(isEditMode);

    const [form, setForm] = useState({
        valor: '',
        descripcion: ''
    });

    const fetchEstado = useCallback(async () => {
        if (!isEditMode) return;
        try {
            setFetching(true);
            const data = await getEstadoCuentaById(token, id);
            setForm({
                valor: data.valor,
                descripcion: data.descripcion || ''
            });
        } catch (error) {
            toast.error('Error al cargar datos del estado');
            navigate('/parametrizacion/estados-cuenta');
        } finally {
            setFetching(false);
        }
    }, [id, isEditMode, token, navigate]);

    useEffect(() => {
        fetchEstado();
    }, [fetchEstado]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!form.valor.trim()) {
            toast.error('El código (valor) es obligatorio');
            return;
        }

        try {
            setLoading(true);
            const dataToSend = {
                valor: form.valor.toUpperCase(),
                descripcion: form.descripcion
            };

            if (isEditMode) {
                await updateEstadoCuenta(token, id, dataToSend);
                toast.success('Estado actualizado correctamente');
            } else {
                await createEstadoCuenta(token, dataToSend);
                toast.success('Estado creado con éxito');
            }
            setTimeout(() => navigate('/parametrizacion/estados-cuenta'), 1000);
        } catch (error) {
            toast.error('Error al guardar los cambios. Verifique que el código no esté duplicado.');
        } finally {
            setLoading(false);
        }
    };

    if (fetching) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 animate-in fade-in">
                <RefreshCw className="w-10 h-10 animate-spin text-blue-600" />
                <p className="text-slate-500 font-bold uppercase text-xs tracking-widest italic">Sincronizando con el servidor...</p>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto bg-white shadow-xl border border-slate-300 mb-10 overflow-hidden rounded-none animate-in fade-in duration-500">
            <Toaster position="top-right" />

            {/* Header Enterprise Estilo Oracle APEX */}
            <div className="bg-slate-700 p-4 flex items-center justify-between text-white shadow-inner border-b border-slate-800">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/parametrizacion/estados-cuenta')}
                        className="p-1.5 hover:bg-slate-600 transition-colors text-slate-300 hover:text-white"
                    >
                        <ArrowLeft className="w-6 h-6 shadow-sm" />
                    </button>
                    <div>
                        <h1 className="text-lg font-black tracking-tight uppercase italic">
                            {isEditMode ? 'Mantenimiento: ' + form.descripcion : 'Definición de Nuevo Estado de Cuenta'}
                        </h1>
                        <p className="text-[9px] text-slate-400 font-bold uppercase tracking-[0.3em]">Módulo de Configuración Operativa de Ahorros</p>
                    </div>
                </div>
                <div className="bg-slate-600 px-3 py-1 text-[10px] font-black uppercase border border-slate-500 shadow-sm">Mv-EST-CUEN-0.1</div>
            </div>

            <form onSubmit={handleSubmit} className="p-8 bg-[#f8fafc]">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">

                    {/* BLOQUE IZQUIERDO: Definición del Estado */}
                    <div className="space-y-8">
                        <div className="bg-white border border-slate-200 p-6 relative overflow-hidden shadow-sm">
                            <div className="absolute top-0 left-0 w-1 h-full bg-slate-700"></div>
                            <div className="flex items-center gap-2 mb-6 border-b border-slate-100 pb-2">
                                <FileText className="w-4 h-4 text-slate-400" />
                                <h3 className="text-[11px] font-black text-slate-700 uppercase tracking-widest">Información del Estado</h3>
                            </div>

                            <div className="space-y-6">
                                <div>
                                    <label className="block text-[10px] font-black text-slate-500 mb-2 uppercase tracking-tight">Código del Estado (Valor) <span className="text-red-500">*</span></label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            required
                                            maxLength="20"
                                            className="w-full pl-4 pr-10 py-3 border border-slate-300 focus:border-blue-600 outline-none font-black uppercase bg-slate-50 focus:bg-white transition-all text-[13px] rounded-none shadow-sm"
                                            placeholder="Ej: ACT, BLO, CER"
                                            value={form.valor}
                                            onChange={(e) => setForm({ ...form, valor: e.target.value.toUpperCase() })}
                                        />
                                        <Hash className="absolute right-3 top-3.5 w-4 h-4 text-slate-300" />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-[10px] font-black text-slate-500 mb-2 uppercase tracking-tight">Descripción / Significado Operativo</label>
                                    <input
                                        type="text"
                                        required
                                        className="w-full px-4 py-3 border border-slate-300 focus:border-blue-600 outline-none font-bold uppercase bg-white focus:bg-white transition-all text-[13px] rounded-none shadow-sm"
                                        placeholder="Ej: ACTIVO, BLOQUEADO POR JUDICIAL, CERRADO"
                                        value={form.descripcion}
                                        onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* BLOQUE DERECHO: Guía Operacional */}
                    <div className="space-y-6 bg-slate-200/50 p-6 border border-slate-300 shadow-inner">
                        <div className="text-center mb-4">
                            <h2 className="text-xs font-black text-slate-700 uppercase tracking-[0.2em]">Guía Operativa</h2>
                            <div className="h-1 w-12 bg-blue-600 mx-auto mt-1"></div>
                        </div>

                        <div className="space-y-4">
                            <div className="bg-white border border-slate-200 p-5 shadow-sm hover:border-blue-400 transition-all">
                                <h3 className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3 flex items-center gap-1 border-b border-slate-100 pb-1">
                                    <AlertCircle className="w-3.5 h-3.5 text-blue-500" />
                                    Uso en Cuentas
                                </h3>
                                <p className="text-[11px] text-slate-600 font-bold uppercase leading-relaxed">
                                    Este estado determinará si la cuenta puede recibir depósitos o extracciones. Tenga cuidado al modificar estados que ya tienen saldo.
                                </p>
                            </div>

                            <div className="bg-amber-50 border border-amber-200 p-4 shadow-sm flex items-start gap-3">
                                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-1" />
                                <div className="text-[10px] text-amber-800 font-bold uppercase leading-relaxed">
                                    <p className="mb-1">⚠️ AVISO PARA TESORERÍA:</p>
                                    La eliminación de estados está restringida si existen cuentas asociadas. Se recomienda utilizar una nomenclatura clara para evitar errores en caja.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer del Formulario */}
                <div className="mt-12 pt-6 border-t border-slate-300 flex justify-between items-center">
                    <button
                        type="button"
                        onClick={() => navigate('/parametrizacion/estados-cuenta')}
                        className="px-8 py-2 border border-slate-300 text-slate-500 hover:bg-slate-200 font-black text-[11px] uppercase transition-all shadow-sm rounded-none"
                    >
                        Cancelar y Salir
                    </button>

                    <button
                        type="submit"
                        disabled={loading}
                        className="px-12 py-3 bg-[#0066cc] text-white shadow-lg hover:bg-[#004d99] transition-all font-black text-[12px] uppercase tracking-[0.1em] flex items-center gap-2 border border-[#0052a3] shadow-[0_4px_10px_rgba(0,102,204,0.3)] disabled:opacity-50"
                    >
                        {loading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
                        {isEditMode ? 'Consolidar Cambios' : 'Confirmar Definición'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default EstadoCuentaForm;
